import { useQuery } from "@apollo/client"
import { CheckCircle2, Info } from "lucide-react"
import { Link, useParams, useSearchParams } from "react-router-dom"
import { useEffect } from "react"
import { gql } from "~/__generated__"
import { useDocumentTitle } from "~/common/use-document-title"
import { lettermemeLatestPath } from "~/common/paths"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/errors"
import { ScreenLoading } from "~/ui/screen-loading"
import { useTrackingCode } from "~/common/use-tracking-code"
import { useSafeMutation } from "~/common/use-safe-mutation"

const lettermemeSubscribedQuery = gql(/* GraphQL */ `
  query lettermemeSubscribed($urlPath: String!) {
    lettermeme(urlPath: $urlPath) {
      id
      name
      urlPath
      trackingCode
    }
  }
`)

const stripeCheckoutSessionSyncMutation = gql(/* GraphQL */ `
  mutation StripeCheckoutSessionSync($input: StripeCheckoutSessionSyncInput!) {
    stripeCheckoutSessionSync(input: $input) {
      success
    }
  }
`)

export const LettermemeSubscribedScreen: React.FC = () => {
  const { urlPath } = useParams<{ urlPath: string }>()
  const [searchParams] = useSearchParams()
  const checkoutSessionId = searchParams.get("session_id")

  const { data, error, loading } = useQuery(lettermemeSubscribedQuery, {
    variables: { urlPath: urlPath! },
    skip: !urlPath,
  })

  const [syncCheckoutSession, { loading: syncing }] = useSafeMutation(
    stripeCheckoutSessionSyncMutation
  )

  useEffect(() => {
    if (!checkoutSessionId) return

    syncCheckoutSession({
      variables: {
        input: { checkoutSessionId },
      },
    })
  }, [checkoutSessionId, syncCheckoutSession])

  useDocumentTitle(
    data?.lettermeme ? `Subscribed to ${data.lettermeme.name}` : "Subscribed to Lettermeme"
  )
  useTrackingCode(data?.lettermeme?.trackingCode)

  if (error) return <GraphqlError error={error} />
  if (loading || syncing) return <ScreenLoading />

  if (!data?.lettermeme) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black p-8">
        <div className="text-center text-white">
          <h1 className="mb-4 text-2xl font-bold">Lettermeme not found</h1>
          <p>The lettermeme you're looking for doesn't exist.</p>
        </div>
      </div>
    )
  }

  const lettermeme = data.lettermeme

  return (
    <div className="flex min-h-screen items-center justify-center bg-black p-8">
      <div className="flex w-full max-w-3xl flex-col gap-6">
        <div className="mx-auto flex shrink flex-col justify-center gap-2">
          <div className="mb-2 flex justify-center">
            <CheckCircle2 className="h-24 w-24 text-white" strokeWidth={0.5} />
          </div>
          <h1 className="text-3xl font-bold text-white">Welcome to {lettermeme.name}</h1>
          <p className="">You're all set. Here's what happens next:</p>
        </div>

        <div className="max-w-4xl space-y-4 p-8 text-white">
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <Info className="h-6 w-6" />
            </div>
            <div>
              <h2 className="mb-2 text-xl font-semibold">Check your inbox</h2>
              <p className="text-gray-400">
                We just sent you a welcome email with some useful information
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <Info className="h-6 w-6" />
            </div>
            <div>
              <h2 className="mb-2 text-xl font-semibold">Expect daily newsletters</h2>
              <p className="text-gray-400">
                Each day, you'll get fresh illustrated lettermemes delivered to your inbox
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <Info className="h-6 w-6" />
            </div>
            <div>
              <h2 className="mb-2 text-xl font-semibold">Explore the index</h2>
              <p className="text-gray-400">Browse past lettermemes and never miss a moment</p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl px-8">
          <Link to={lettermemeLatestPath({ urlPath: lettermeme.urlPath })} className="block">
            <Button variant="outline" className="w-full bg-white py-6 text-lg text-black">
              View Recent Lettermeme
            </Button>
          </Link>
        </div>
      </div>
    </div>
  )
}
